import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import {
  getAdminJobBoard,
  closeJobPosting,
  updateInterviewStatus,
  addJobPostingAdmin,
  getAllJobApplications,
  type AdminJobEntry,
  type JobApplicationRow,
} from '../../lib/api';

const INTERVIEW_STATUSES = ['Applied', 'Interview Scheduled', 'Interview Done', 'Offer Received', 'Rejected'];

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  'Applied':             { bg: '#eff6ff', text: '#1d4ed8' },
  'Interview Scheduled': { bg: '#fefce8', text: '#854d0e' },
  'Interview Done':      { bg: '#f0fdf4', text: '#166534' },
  'Offer Received':      { bg: '#f0fdf4', text: '#15803d' },
  'Rejected':            { bg: '#fef2f2', text: '#b91c1c' },
};

const ALL_STUDENTS = 'All Students';
const COURSES = [ALL_STUDENTS, 'Full Stack Analytics', 'AIML', 'Data Science'];

const EMPTY_FORM = { title: '', company: '', description: '', jobLink: '', targetCourses: [] as string[] };

type PageTab = 'jobs' | 'interviews' | 'applications';

const AdminJobBoard: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [jobs, setJobs] = useState<AdminJobEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState<Record<string, 'applied' | 'notApplied' | 'alert'>>({});
  const [closing, setClosing] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [pageTab, setPageTab] = useState<PageTab>('jobs');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [allApps, setAllApps] = useState<JobApplicationRow[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appSearch, setAppSearch] = useState('');

  useEffect(() => { loadJobs(); }, []);

  async function loadJobs() {
    setLoading(true);
    try {
      const res = await getAdminJobBoard(adminPassword);
      setJobs(res.jobs ?? []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load job board');
    } finally {
      setLoading(false);
    }
  }

  async function handleClose(jobId: string) {
    setClosing(jobId);
    try {
      await closeJobPosting(adminPassword, jobId);
      toast.success('Job closed');
      setJobs(prev => prev.map(j => j._id === jobId ? { ...j, status: 'Closed' } : j));
    } catch (err: any) {
      toast.error(err.message || 'Failed to close job');
    } finally {
      setClosing(null);
    }
  }

  function toggleCourse(course: string) {
    setForm(f => {
      if (course === ALL_STUDENTS) {
        return { ...f, targetCourses: f.targetCourses.includes(ALL_STUDENTS) ? [] : [ALL_STUDENTS] };
      }
      const without = f.targetCourses.filter(c => c !== ALL_STUDENTS && c !== course);
      return { ...f, targetCourses: f.targetCourses.includes(course) ? without : [...without, course] };
    });
  }

  async function handlePost(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.company.trim()) { toast.error('Title and company are required'); return; }
    if (form.targetCourses.length === 0) { toast.error('Select at least one target course'); return; }
    setSubmitting(true);
    try {
      await addJobPostingAdmin(adminPassword, form.title.trim(), form.company.trim(), form.description.trim(), form.jobLink.trim(), form.targetCourses);
      toast.success('Job posted and students notified via email!');
      setForm({ ...EMPTY_FORM });
      setShowForm(false);
      loadJobs();
    } catch (err: any) {
      toast.error(err.message || 'Failed to post job');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStatusChange(applicationId: string, status: string, jobId: string) {
    setUpdatingStatus(applicationId);
    try {
      await updateInterviewStatus(adminPassword, applicationId, status);
      setJobs(prev => prev.map(j =>
        j._id === jobId
          ? { ...j, applications: j.applications.map(a => a._id === applicationId ? { ...a, interviewStatus: status } : a) }
          : j
      ));
    } catch (err: any) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  }

  function getJobTab(jobId: string) { return expandedTab[jobId] ?? 'applied'; }
  function setJobTab(jobId: string, tab: 'applied' | 'notApplied' | 'alert') {
    setExpandedTab(prev => ({ ...prev, [jobId]: tab }));
  }

  async function loadAllApps() {
    setAppsLoading(true);
    try {
      const res = await getAllJobApplications(adminPassword);
      setAllApps(res.applications ?? []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load applications');
    } finally {
      setAppsLoading(false);
    }
  }

  const activeCount = jobs.filter(j => j.status === 'Active').length;

  // All interview updates across all jobs (non-Applied statuses)
  const interviewUpdates = jobs.flatMap(j =>
    j.applications
      .filter(a => a.interviewStatus && a.interviewStatus !== 'Applied')
      .map(a => ({ ...a, jobTitle: j.title, company: j.company, jobId: j._id }))
  );

  return (
    <TrackerLayout>
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#2b2240' }}>Job Board</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {jobs.length} total · {activeCount} active
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => { setShowForm(s => !s); setPageTab('jobs'); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
            style={{ backgroundColor: showForm ? '#6b7280' : '#F0B429', color: showForm ? '#fff' : '#2b2240' }}
          >
            {showForm ? 'Cancel' : '+ Post a Job'}
          </button>
          {([
            { key: 'jobs', label: 'All Jobs' },
            { key: 'interviews', label: `Interviews (${interviewUpdates.length})` },
            { key: 'applications', label: 'All Applications' },
          ] as { key: PageTab; label: string }[]).map(t => (
            <button
              key={t.key}
              onClick={() => { setPageTab(t.key); if (t.key === 'applications' && allApps.length === 0) loadAllApps(); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors"
              style={pageTab === t.key
                ? { backgroundColor: '#2b2240', color: '#F0B429' }
                : { backgroundColor: '#f3f4f6', color: '#374151' }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Post a Job form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-base font-semibold mb-4" style={{ color: '#2b2240' }}>New Job Opening</h2>
          <form onSubmit={handlePost} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Job Title *</label>
                <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Data Analyst Intern" required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Company *</label>
                <input type="text" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="e.g. Infosys" required
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Job Link</label>
              <input type="url" value={form.jobLink} onChange={e => setForm(f => ({ ...f, jobLink: e.target.value }))}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Job details, requirements, salary range…" rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#2b2240] resize-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Target Courses *</label>
              <div className="flex flex-wrap gap-2">
                {COURSES.map(c => (
                  <button key={c} type="button" onClick={() => toggleCourse(c)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                    style={form.targetCourses.includes(c)
                      ? { backgroundColor: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                      : { backgroundColor: '#fff', color: '#374151', borderColor: '#d1d5db' }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400">Email notifications will be sent to all eligible students automatically.</p>
            <button type="submit" disabled={submitting}
              className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#F0B429', color: '#2b2240' }}>
              {submitting ? 'Posting…' : 'Post Job & Notify Students'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : pageTab === 'interviews' ? (

        /* ── INTERVIEWS TAB ── */
        <div className="space-y-3">
          {interviewUpdates.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No interview updates yet.</div>
          ) : (
            interviewUpdates.map(a => {
              const s = STATUS_STYLE[a.interviewStatus!] ?? STATUS_STYLE['Applied'];
              return (
                <div key={a._id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900">{a.studentName}</p>
                    <p className="text-xs text-gray-500">{a.studentEmail} · {a.course}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{a.jobTitle} @ {a.company}</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: s.bg, color: s.text }}>
                    {a.interviewStatus}
                  </span>
                </div>
              );
            })
          )}
        </div>

      ) : pageTab === 'applications' ? (

        /* ── ALL APPLICATIONS TAB ── */
        <div className="space-y-4">
          <div className="flex gap-3">
            <input
              value={appSearch} onChange={e => setAppSearch(e.target.value)}
              placeholder="Search student or job…"
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
            />
            <button onClick={loadAllApps} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50">↻ Refresh</button>
          </div>
          {appsLoading ? (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          ) : allApps.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No applications found.</div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Student', 'Course', 'Job / Company', 'Status', 'Applied At'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allApps.filter(a => {
                      const q = appSearch.toLowerCase();
                      return !q || a.studentName.toLowerCase().includes(q) || a.jobTitle.toLowerCase().includes(q) || a.company.toLowerCase().includes(q);
                    }).map((a, i) => {
                      const s = STATUS_STYLE[a.interviewStatus ?? 'Applied'] ?? STATUS_STYLE['Applied'];
                      return (
                        <tr key={a._id} className={`border-t border-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{a.studentName}</p>
                            <p className="text-xs text-gray-400">{a.studentEmail}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{a.course || '—'}</td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-700">{a.jobTitle}</p>
                            <p className="text-xs text-gray-400">{a.company}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: s.bg, color: s.text }}>
                              {a.interviewStatus ?? 'Applied'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400">
                            {a.appliedAt ? new Date(a.appliedAt).toLocaleDateString('en-IN') : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No job postings yet.</div>
      ) : (

        /* ── JOBS TAB ── */
        <div className="space-y-4">
          {jobs.map(job => {
            const isOpen = expanded === job._id;
            const pct = job.totalEligible > 0 ? Math.round((job.appliedCount / job.totalEligible) * 100) : 0;
            const alertStudents = job.threeDaysOld ? (job.notAppliedStudents ?? []) : [];
            const currentJobTab = getJobTab(job._id);

            return (
              <div key={job._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{job.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          job.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>{job.status}</span>
                        {job.threeDaysOld && alertStudents.length > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-600">
                            ⚠ {alertStudents.length} not applied 3+ days
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{job.company} · {job.postedByName}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {job.targetCourses?.map(c => (
                          <span key={c} className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded-full">{c}</span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-2xl font-bold" style={{ color: '#2b2240' }}>{pct}%</p>
                        <p className="text-xs text-gray-500">{job.appliedCount} / {job.totalEligible} applied</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setExpanded(isOpen ? null : job._id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                          {isOpen ? 'Hide' : 'Details'}
                        </button>
                        {job.status === 'Active' && (
                          <button
                            onClick={() => handleClose(job._id)}
                            disabled={closing === job._id}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                          >
                            {closing === job._id ? '…' : 'Close Job'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#22c55e' : pct >= 40 ? '#F0B429' : '#ef4444' }}
                    />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    {/* Sub-tabs */}
                    <div className="flex border-b border-gray-200 px-5">
                      {([
                        { key: 'applied',    label: `Applied (${job.appliedCount})` },
                        { key: 'notApplied', label: `Not Applied (${job.notAppliedCount})` },
                        ...(job.threeDaysOld && alertStudents.length > 0
                          ? [{ key: 'alert', label: `⚠ 3+ Days (${alertStudents.length})` }]
                          : []),
                      ] as { key: 'applied' | 'notApplied' | 'alert'; label: string }[]).map(t => (
                        <button
                          key={t.key}
                          onClick={() => setJobTab(job._id, t.key)}
                          className="px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors"
                          style={{
                            borderColor: currentJobTab === t.key ? '#2b2240' : 'transparent',
                            color: currentJobTab === t.key ? '#2b2240' : '#6b7280',
                          }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>

                    <div className="p-5">
                      {/* Applied list */}
                      {currentJobTab === 'applied' && (
                        job.applications.length === 0 ? (
                          <p className="text-sm text-gray-400">No applications yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {job.applications.map(app => {
                              const s = STATUS_STYLE[app.interviewStatus ?? 'Applied'] ?? STATUS_STYLE['Applied'];
                              return (
                                <div key={app._id} className="bg-white rounded-lg border border-gray-200 px-4 py-3">
                                  <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                      <p className="text-sm font-semibold text-gray-800">{app.studentName}</p>
                                      <p className="text-xs text-gray-400">{app.studentEmail} · {app.course}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {app.screenshotBase64 && (
                                        <a href={app.screenshotBase64} target="_blank" rel="noreferrer"
                                          className="text-xs text-blue-600 hover:underline">Screenshot</a>
                                      )}
                                      <select
                                        value={app.interviewStatus ?? 'Applied'}
                                        disabled={updatingStatus === app._id}
                                        onChange={e => handleStatusChange(app._id, e.target.value, job._id)}
                                        className="text-xs px-2 py-1.5 rounded-lg border font-semibold outline-none cursor-pointer"
                                        style={{ backgroundColor: s.bg, color: s.text, borderColor: s.text + '40' }}
                                      >
                                        {INTERVIEW_STATUSES.map(st => (
                                          <option key={st} value={st}>{st}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )
                      )}

                      {/* Not applied list */}
                      {currentJobTab === 'notApplied' && (
                        (job.notAppliedStudents ?? []).length === 0 ? (
                          <p className="text-sm text-gray-400">All eligible students have applied!</p>
                        ) : (
                          <div className="space-y-2">
                            {job.notAppliedStudents.map(s => (
                              <div key={s.studentId} className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{s.name}</p>
                                  <p className="text-xs text-gray-400">{s.email} · {s.course}</p>
                                </div>
                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Not Applied</span>
                              </div>
                            ))}
                          </div>
                        )
                      )}

                      {/* 3+ days alert list */}
                      {currentJobTab === 'alert' && (
                        <div className="space-y-2">
                          <p className="text-xs text-red-600 font-semibold mb-3">
                            These students haven't applied even though the job was posted 3+ days ago.
                          </p>
                          {alertStudents.map(s => (
                            <div key={s.studentId} className="bg-red-50 rounded-lg border border-red-200 px-4 py-2.5 flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-800">{s.name}</p>
                                <p className="text-xs text-gray-400">{s.email} · {s.course}</p>
                              </div>
                              <span className="text-xs text-red-600 font-bold">⚠ 3+ Days</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </TrackerLayout>
  );
};

export default AdminJobBoard;
